import React from 'react';
import { LuLock } from 'react-icons/lu';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { LifecycleStatus } from '@ndvr/portfolio-api';

import { Page } from '@/components/ui/page-layout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useRequiredParams } from 'utils';
import { useGlobalAccount } from 'views/household/household-queries';
import { PortfolioPageHeader } from 'views/portfolio/PortfolioPage/PortfolioPageHeader';

import { useAccountGroupProfileWithAumIncluded } from '../account-aum-queries';

export const PortfolioPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { globalAccountGUID, accountGroupGUID } = useRequiredParams(['globalAccountGUID', 'accountGroupGUID']);

    const { data: household } = useGlobalAccount(globalAccountGUID);

    const { data: accountGroupProfile } = useAccountGroupProfileWithAumIncluded(accountGroupGUID);

    // Define the tabs with proper routing paths
    const tabs = [
        { value: 'overview', label: 'Overview' },
        { value: 'target-allocation', label: 'Target Allocation' },
        { value: 'liquidity-planning', label: 'Liquidity Planning' },
        { value: 'tax-profile', label: 'Tax Profile' },
        { value: 'equity-preferences', label: 'Equity Preferences' },
        { value: 'asset-location', label: 'Asset Location' },
    ];

    // Get the current active tab from the URL path
    const getCurrentTab = () => {
        const pathSegments = location.pathname.split('/');

        // Look for main tab in the path segments
        for (const tab of tabs) {
            if (pathSegments.includes(tab.value)) {
                return tab.value;
            }
        }

        // Default to overview if no tab found
        return 'overview';
    };

    // Handle tab change and navigate to the corresponding route
    const handleTabChange = (value: string) => {
        navigate(value);
    };

    return (
        <Page>
            {/* Sticky Header */}
            <div className="shrink-0">
                <PortfolioPageHeader
                    householdName={household?.globalAccountNamePlain || ''}
                    householdGuid={globalAccountGUID}
                    portfolioName={accountGroupProfile?.accountGroup.accountGroupName.plainText || ''}
                    portfolioGuid={accountGroupProfile?.accountGroup.accountGroupGUID || ''}
                    portfolioStatus={accountGroupProfile?.accountGroup.lifecycleStatus || LifecycleStatus.DRAFT}
                />
            </div>

            {/* Tabs Container */}
            <div className="flex flex-1 flex-col overflow-hidden">
                <Tabs value={getCurrentTab()} onValueChange={handleTabChange} className="flex h-full flex-col">
                    {/* Sticky Tab List */}
                    <div className="sticky top-0 z-10 flex-shrink-0 bg-white pt-4">
                        <TabsList className="flex border-gray-300">
                            <TabsTrigger value="overview">Overview</TabsTrigger>
                            <TabsTrigger value="target-allocation">Target Allocation</TabsTrigger>
                            <TabsTrigger value="liquidity-planning">Liquidity Planning</TabsTrigger>
                            <TabsTrigger value="tax-profile">Tax Profile</TabsTrigger>
                            <TabsTrigger value="equity-preferences">Equity Preferences</TabsTrigger>
                            {!accountGroupProfile?.accountGroup.allocationGUID ? (
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <div>
                                                <TabsTrigger value="asset-location" disabled={true}>
                                                    Asset Location
                                                    <span className="ml-2 inline-block align-middle">
                                                        <LuLock className="text-gray-400" />
                                                    </span>
                                                </TabsTrigger>
                                            </div>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>Only available for Portfolio-level allocations</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            ) : (
                                <TabsTrigger value="asset-location">Asset Location</TabsTrigger>
                            )}
                        </TabsList>
                    </div>

                    {/* Scrollable Content */}
                    <TabsContent value={getCurrentTab()} className="w-full flex-1 overflow-y-auto pt-6 pr-5 pb-6 pl-5">
                        <Outlet />
                    </TabsContent>
                </Tabs>
            </div>
        </Page>
    );
};
